<script lang="ts">
  import { browser } from '$app/environment';
  import Button from '$lib/components/reusable/Button.svelte';
  import ProfileBadges from '$lib/components/reusable/ProfileBadges.svelte';
  import { type ProjectComment } from '$shared/lib';
  import { timeSince } from '$utils';
  import Avatar from '../../images/Avatar.svelte';
  import CommentDropdown from './CommentDropdown.svelte';
  import type { User } from 'src/routes/(app)/+layout.server';
  import ReplyIcon from '$icons/messages/ReplyIcon.svelte';
  import { cn } from '$lib/utils/misc';
  import CommentLikeButton from './CommentLikeButton.svelte';
  import EditComment from './EditComment.svelte';
  import ReplyToComment from './ReplyToComment.svelte';
  import LoadMoreReplies from './LoadMoreReplies.svelte';
  import RichTextRender from '../../posts/RichTextRender.svelte';

  export let currentUser: User | null;
  export let comment: ProjectComment;
  export let forceCloseReply = false;

  let showEditComment = false;
  let showReplyInput = false;

  $: if (forceCloseReply) {
    showReplyInput = false;
  }
</script>

{#if showEditComment}
  <EditComment {comment} bind:showEditComment />
{:else}
  <div class={cn('w-full', $$props.class)}>
    <div class="relative flex w-full items-start gap-x-2">
      {#if comment.replies.length > 0}
        <!-- Vertical line -->
        <div class="bg-gray-dark-4 absolute bottom-0 left-2.5 top-10 w-px" />
      {/if}
      {#if comment.isDeleted}
        <!-- Deleted comment indicator -->
        <h2 class="text-gray-dark-11 pb-4 pt-3">
          <RichTextRender content={comment.body} />
        </h2>
      {:else}
        <div class="mt-1.5 aspect-square h-5">
          <a href="/profiles/{comment.user.username}">
            {#if browser}
              {#key comment.user.username}
                <Avatar
                  src={comment.user.avatarUrl}
                  seed={comment.user.username}
                  class="h-6 w-6 rounded"
                />
              {/key}
            {/if}
          </a>
        </div>
        <div class="relative flex w-full flex-1 flex-col gap-4">
          <div class="flex items-center justify-between">
            <div
              class="font-inter text-gray-dark-12 mt-1 flex items-center gap-2 py-1 text-xs leading-4"
            >
              <a
                href="/profiles/{comment.user.username}"
                class="flex flex-row items-center gap-x-2"
              >
                <h3 class="whitespace-nowrap text-sm">
                  {comment.user.displayName}
                </h3>
                <h4 class="text-gray-dark-11 whitespace-nowrap text-xs">
                  @{comment.user.username}
                </h4>
              </a>
              <ProfileBadges roles={comment.user.accountRoles} batches={comment.user.batches} />
              <div class="square !bg-gray-dark-6"></div>
              <h3 class="text-gray-dark-11">
                {comment.createdAt && timeSince(new Date(comment.createdAt))}
              </h3>
            </div>
            <CommentDropdown {currentUser} {comment} bind:showEditComment />
          </div>
          <!-- Comment body -->
          {#if browser}
            <RichTextRender content={comment.body} />
          {/if}
          <!-- Comment actions -->
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-x-5">
              <CommentLikeButton {comment} {currentUser} />
              <Button
                size="sm"
                transparent
                class="!px-0"
                disabled={!currentUser}
                on:click={() => (showReplyInput = true)}
              >
                <ReplyIcon />
                <span>Reply</span>
              </Button>
            </div>
            <!-- Load more replies (if any) -->
            {#if comment.hasMoreReplies}
              <LoadMoreReplies {comment} />
            {/if}
          </div>
          {#if showReplyInput}
            <ReplyToComment bind:showReplyInput parentComment={comment} {currentUser} />
          {/if}
        </div>
      {/if}
    </div>

    <!-- Replies section -->
    {#if comment.replies && comment.replies.length > 0}
      <div class="relative">
        {#each comment.replies as childComment, i}
          <div class="relative ml-12 pt-3">
            <!-- Individual reply vertical line -->
            <div
              class={cn(
                'bg-gray-dark-4 absolute -left-[38px] -top-[9px] w-px',
                i === comment.replies.length - 1 && !comment.hasMoreReplies ? 'h-10' : 'bottom-0',
              )}
            />
            <!-- Horizontal connector -->
            <div
              class="border-gray-dark-4 absolute -left-[38px] top-[30px] h-px w-6 rounded-bl-sm border-b border-l"
            />
            <svelte:self comment={childComment} {currentUser} />
          </div>
        {/each}
      </div>
    {/if}
  </div>
{/if}
